import { Module } from 'vuex';
import {
    SET_COMMENTARY_SECTIONS,
    SET_TICKET_DETAIL,
    SET_WORKFLOW_STEPS,
    SET_TAPE_DELIVERIES,
    SET_TAPE_DELIVERY,
    SET_TAPE_CHANGE_TICKET_DETAIL,
} from '@/modules/tickets/shared/store/tickets.mutations';
import {
    FETCH_TICKET,
    FETCH_TAPE_DELIVERIES,
    CREATE_ACTION_OVER_ACCESS_PERMISSION,
    FETCH_TAPE_DELIVERY_BY_ID,
    SET_TAPE_CHANGE_TICKET,
} from '@/modules/tickets/shared/store/tickets.actions';
import {
    CAN_APPROVE_ACCESS_PERMISSION,
    GET_ACCESS_PERMISSION_MOTIVES,
    GET_TICKET_DETAIL,
    GET_WORKFLOW_STEPS_ID,
    GET_TAPE_DELIVERIES,
    GET_TAPE_DELIVERIES_OPTIONS,
    CAN_EMIT_COMMENTS_OVER_ACCESS_PERMISSION_APPROVAL,
    GET_TAPE_DELIVERY,
    GET_TAPE_CHANGE_TICKET,
} from '@/modules/tickets/shared/store/tickets.getters';
import ticketsRepository from '@/modules/tickets/shared/repositories/tickets.repository';
import accessPermissionsRepository from '@/modules/tickets/shared/repositories/access-permissions.repository';
import tapeDeliveriesRepository from '@/components/tickets/repositories/tape-deliveries.repository';
import { WorkflowActions } from '@/modules/shared/shared/domain/constants/constants';
import moment from 'moment';
import { TicketsDatatableItem } from '@/modules/tickets/shared/domain/tickets-datatable-item';
import networkSecurityAndInternet from '@/modules/tickets/requirement/network-security-internet/store/network-security-internet';
import managementAndManagedServices from '@/modules/tickets/requirement/management-managed-services/store/management-managed-services';
import linkManagement from '@/modules/tickets/incidents/link-management/store/link-management';
import networkSecurityInternetIncident from '@/modules/tickets/incidents/network-security-internet/store/network-security-internet-incident';
import processingAndManagedManagement from '@/modules/tickets/incidents/processing-and-managed-management/store/processing-and-managed-management';

const tickets: Module<any, any> = {
    namespaced: true,
    modules: {
        networkSecurityAndInternet,
        managementAndManagedServices,
        linkManagement,
        networkSecurityInternetIncident,
        processingAndManagedManagement,
    },
    state: {
        commentarySections: [],
        ticketDetail: {},
        workflowSteps: [],
        tapeDelivery: {},
        tapeDeliveries: {
            start: 0,
            limit: 0,
            total: 0,
            pages: 1,
            results: [],
        },
    },
    getters: {
        [CAN_APPROVE_ACCESS_PERMISSION](state) {
            return state.workflowSteps.some(
                workflowStep => workflowStep.workflowAction === WorkflowActions.APPROVE.id,
            );
        },
        [CAN_EMIT_COMMENTS_OVER_ACCESS_PERMISSION_APPROVAL](state) {
            return state.workflowSteps.some(
                workflowStep => workflowStep.workflowAction === WorkflowActions.REQUEST_CHANGES.id,
            );
        },
        [GET_TICKET_DETAIL](state) {
            // TODO: Definir interfaz para este objeto
            return state.ticketDetail;
        },
        [GET_WORKFLOW_STEPS_ID](state) {
            return state.workflowSteps.map(({ id }) => id);
        },
        [GET_ACCESS_PERMISSION_MOTIVES](state): number[] {
            if (state.ticketDetail.accessPermission) {
                return state.ticketDetail.accessPermission.accessPermissionMotives.map(
                    accessPermissionMotive => accessPermissionMotive.accessMotive.id,
                );
            }
            return [];
        },
        [GET_TAPE_DELIVERIES](state): TicketsDatatableItem[] {
            if (state.tapeDeliveries.results) {
                return state.tapeDeliveries.results.map(i => ({
                    id: i.id,
                    date: moment(i.date).format('DD/MM/YYYY hh:mm A'),
                    customer: i.customer ? i.customer.name : undefined,
                    contactFullName: `${i.contact.name} ${i.contact.lastName}`,
                    contactCi: i.contact.ci,
                    ticketId: i.ticketId,
                }));
            }

            return [];
        },
        [GET_TAPE_DELIVERY](state) {
            return state.tapeDelivery;
        },
        [GET_TAPE_DELIVERIES_OPTIONS](state) {
            return state.tapeDeliveries;
        },
        [GET_TAPE_CHANGE_TICKET](state) {
            return state.tapeChangeTicketDetail;
        },
    },
    mutations: {
        [SET_COMMENTARY_SECTIONS](state, commentarySections) {
            // TODO: Definir interfaz para este objeto
            state.commentarySections = commentarySections;
        },
        [SET_TICKET_DETAIL](state, ticketDetail) {
            // TODO: Definir interfaz para este objeto
            state.ticketDetail = ticketDetail;
        },
        [SET_WORKFLOW_STEPS](state, workflowSteps) {
            // TODO: Definir interfaz para este objeto
            state.workflowSteps = workflowSteps;
        },
        [SET_TAPE_DELIVERIES](state, tapeDeliveries) {
            state.tapeDeliveries = tapeDeliveries;
        },
        [SET_TAPE_DELIVERY](state, tapeDelivery) {
            state.tapeDelivery = tapeDelivery;
        },
        [SET_TAPE_CHANGE_TICKET_DETAIL](state, ticket) {
            state.tapeChangeTicketDetail = ticket;
        },
    },
    actions: {
        [SET_TAPE_CHANGE_TICKET]({ commit }, ticket): void {
            commit(SET_TAPE_CHANGE_TICKET_DETAIL, ticket);
        },
        async [FETCH_TAPE_DELIVERIES]({ commit }, options): Promise<boolean> {
            let result: boolean = false;

            try {
                const tapeDeliveries = (await tapeDeliveriesRepository.getTapeDeliveries(options))
                    .data;
                commit(SET_TAPE_DELIVERIES, tapeDeliveries);
                result = true;
            } catch (e) {
                // Nada por los momentos
            }

            return result;
        },
        async [FETCH_TAPE_DELIVERY_BY_ID]({ commit }, tapeDeliveryId): Promise<boolean> {
            let result: boolean = false;

            try {
                const tapeDelivery = (
                    await tapeDeliveriesRepository.getTapeDeliveryById(tapeDeliveryId)
                ).data;
                commit(SET_TAPE_DELIVERY, tapeDelivery);
                result = true;
            } catch (e) {
                // Nada por los momentos
            }

            return result;
        },
        async [FETCH_TICKET](
            { commit },
            { id, accessPermissionId, roleId, customerType, accessMotives },
        ): Promise<boolean> {
            let result: boolean = false;

            try {
                const ticket = await ticketsRepository.getTicketWorkflowDetailById(
                    id,
                    accessPermissionId,
                    roleId,
                    customerType,
                    accessMotives,
                );
                commit(SET_TICKET_DETAIL, ticket.data.ticket);
                commit(SET_COMMENTARY_SECTIONS, ticket.data.commentarySections);
                commit(SET_WORKFLOW_STEPS, ticket.data.workflowSteps);

                result = true;
            } catch (e) {
                // No hacemos nada...
            }

            return result;
        },
        async [CREATE_ACTION_OVER_ACCESS_PERMISSION]({ commit }, action): Promise<boolean> {
            let result: boolean = false;

            try {
                await accessPermissionsRepository.createActionOverAccessPermission(action);

                result = true;
            } catch (e) {
                // No hacemos nada...
            }

            return result;
        },
    },
};

export default tickets;
